<script lang="ts">
	import { convertToETH } from '$lib/service/WalletManager';
	import { parseEther } from '@ethersproject/units';
	import { BigNumber } from 'ethers';
	import { createEventDispatcher } from 'svelte';
	import Button from '../button/Button.svelte';
	import { CloseIcon } from '../icons';
	import DisplayIcon from '../icons/DisplayIcon.svelte';
	import Overlay from '../loading/Overlay.svelte';
	import { notifications } from '../toast/notifications';
	import FontSpan from '../typography/FontSpan.svelte';

	const dispatchClose = createEventDispatcher<{ closed: { closed: boolean; refresh: boolean } }>();

	export let open = false;
	export let amount = '';
	export let currencyName = 'WETH';
	export let contractAddress = '';
	export let network = '';
	export let maxAmount = '';

	const postiveFloatRegEx = /^(?:[1-9]\d*|0)?(?:\.\d+)?$/;

	function toggle(refresh: boolean) {
		open = !open;
		if (!open) {
			amount = '';
			errorMessage = '';
			dispatchClose('closed', { closed: true, refresh });
		}
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			open = false;
			amount = '';
			dispatchClose('closed', { closed: true, refresh: false });
		}
	}

	let depositeInprogress = false;
	async function depositHandler() {
		try {
			depositeInprogress = true;
			await convertToETH(parseEther(amount), contractAddress, network);
			toggle(true);
		} catch (error: any) {
			console.log('convertToETH', error);
			if (error?.data?.message?.length > 0) {
				const metaMaskErrorMessage: string = error?.data?.message;
				if (metaMaskErrorMessage.indexOf('insufficient funds')) {
					notifications.danger('Insufficient funds in wallet', 3000);
				}
			}
		} finally {
			depositeInprogress = false;
		}
	}

	let errorMessage = '';
	function validatePrice(price: string) {
		if (price.length === 0) {
			errorMessage = 'value is required';
			return;
		}
		if (!postiveFloatRegEx.test(price)) {
			errorMessage = 'invalid amount entered';
		}
		if (parseEther(price).gt(BigNumber.from(`${maxAmount}`))) {
			errorMessage = `value is greater then your current ${
				network === 'mainnet' ? 'ETH' : 'MATIC'
			} balance`;
			return;
		}
		errorMessage = '';
	}

	$: errorCSS = !!errorMessage ? 'border-red-500' : 'focus:shadow-outline';
</script>

<svelte:window on:keydown={handleKeydown} />

<Overlay show={depositeInprogress} />
<div
	class="modal fixed w-full h-full top-0 left-0 flex items-center justify-center z-40"
	class:opacity-0={!open}
	class:pointer-events-none={!open}
>
	<div class="modal-overlay absolute w-full h-full" />

	<div class="modal-container z-50">
		<div
			role="button"
			tabindex="0"
			on:click={() => toggle(false)}
			on:keypress={handleKeydown}
			class="modal-close absolute top-0 right-0 cursor-pointer flex flex-col items-center mt-4 mr-4 text-white text-sm z-50"
		>
			<DisplayIcon size={18} icon={CloseIcon} css="fill-current text-white" />
			<span class="text-sm">(Esc)</span>
		</div>
		<div class="modal-content relative text-left">
			<div class="modal-header">
				<div class="model-title-container">
					<div class="flex">
						<FontSpan varient="body-B-03" class="text-white">Add {currencyName}</FontSpan>
					</div>
				</div>
				<div
					role="button"
					tabindex="0"
					on:keypress
					on:click={() => toggle(false)}
					class="close-button cursor-pointer"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="28"
						height="28"
						viewBox="0 0 28 28"
						fill="none"
					>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M21.8249 6.17505C22.2805 6.63066 22.2805 7.36936 21.8249 7.82497L7.82488 21.825C7.36926 22.2806 6.63057 22.2806 6.17496 21.825C5.71935 21.3694 5.71935 20.6307 6.17496 20.1751L20.175 6.17505C20.6306 5.71944 21.3693 5.71944 21.8249 6.17505Z"
							fill="#55DBD7"
						/>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M6.17496 6.17505C6.63057 5.71944 7.36926 5.71944 7.82488 6.17505L21.8249 20.1751C22.2805 20.6307 22.2805 21.3694 21.8249 21.825C21.3693 22.2806 20.6306 22.2806 20.175 21.825L6.17496 7.82497C5.71935 7.36936 5.71935 6.63066 6.17496 6.17505Z"
							fill="#55DBD7"
						/>
					</svg>
				</div>
			</div>
			<!--Body-->
			<div class="flex flex-col modal-body grow w-full">
				<div class="flex mt-2 gap-4 justify-center items-center px-10">
					<input
						placeholder="Amount to deposit"
						data-test-id="price"
						class="p-1 rounded-sm appearance-none outline-none bg-white text-gray-800 w-full input-price {errorCSS}"
						bind:value={amount}
						on:input={() => {
							validatePrice(amount);
						}}
					/>
					<Button
						enable={errorMessage.length === 0}
						type="secondary"
						class="p-2 text-white"
						text="Deposit"
						on:click={depositHandler}
					/>
				</div>
				<div class="flex px-10">
					{#if !!errorMessage}
						<div class="text-red-500 text-sm">{errorMessage}</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
</div>

<style>
	.modal-body {
		padding: 10px 11px 10px 10px;
		gap: 10px;
	}
	.close-button {
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;

		border-radius: 6px;
		box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
	}
	.model-title-container {
		display: flex;
		width: 401px;
		height: 39px;
		padding: 0px 32px 2px 0px;
		justify-content: center;
		align-items: flex-start;
		gap: 12px;
	}
	.modal-header {
		display: flex;
		height: 39px;
		padding: 0px 16px;
		justify-content: center;
		align-items: flex-start;
		gap: 13px;
		flex-shrink: 0;
		align-self: stretch;
	}
	.modal-container {
		display: flex;
		padding: 12px;
		flex-direction: column;
		align-items: center;
		gap: 40px;

		border-radius: 30px;
		border: 1px solid var(--popup-border, #2d2d2d);
		background: var(--bg, #151516);

		/* popup_shadow */
		box-shadow: 0px 0px 20px 0px rgba(189, 189, 189, 0.2);
	}
	.modal-overlay {
		background: rgba(0, 0, 0, 0.8);
		backdrop-filter: blur(8px);
	}

	.modal {
		transition: opacity 0.25s ease;
	}
</style>
